/**
 * Client-side URL-improve fidelity. Keep in sync with
 * functions/src/OpenAI/analyzeFidelity.ts (competitor filter and table merge).
 * Outline assembly for the CI test lives here so enhance can echo a section
 * without a network call.
 */

import {reformatOpenAiResponseToHtml} from '@/utils/string';

/** Grammatical function words. Compared after normalizeLoose (diacritics removed). */
const FUNCTION_WORD = new Set([
    'a', 'i', 'o', 'u', 'k', 's', 'v', 'z', 'na', 'do', 'od', 'po', 'za', 'pro', 'pri',
    've', 'se', 'si', 'je', 'jsou', 'byt', 'ze', 'aby', 'ale', 'nebo', 'jako', 'take',
    'uz', 'jen', 'nez', 'ktery', 'ktera', 'ktere', 'jaky', 'jaka', 'jake', 'co', 'kdo',
    'kde', 'kdy', 'jak', 'tento', 'tato', 'toto', 'sem', 'tam', 'zde', 'ci', 'napr',
    'the', 'and', 'or', 'of', 'to', 'in', 'for', 'with', 'on', 'at', 'by', 'an', 'from',
    'that', 'this', 'these', 'those', 'is', 'are', 'was', 'be', 'it', 'we', 'you', 'your',
    'our', 'not', 'no', 'yes', 'eg', 'vs',
]);

export type AnalyzeSectionInput = {
    part: string;
    heading: string;
    body: string;
    keyword: string;
};

export type AnalyzeSectionEcho = {
    heading?: string;
    body?: string;
};

export type AnalyzeBlock = {h: string; p: string};

export type AssembledAnalyzeArticle = {
    h1: AnalyzeBlock[];
    h2: AnalyzeBlock[];
    h3: AnalyzeBlock[];
    h4: AnalyzeBlock[];
    h5: AnalyzeBlock[];
    h6: AnalyzeBlock[];
    questions: Array<{question: string; answer: string}>;
};

type OutlineLike = {part: string; keyword: string};

export function plainHeadingText(value: unknown): string {
    return String(value || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&#160;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * A trailing fine-print block: short heading, several paragraphs, no table,
 * list, or link cluster. The heading text comes from the page, not from a phrase list.
 */
export function isTrailingLegalBlock(block: {h?: unknown; p?: unknown} | null | undefined): boolean {
    const heading = plainHeadingText(block?.h);
    const body = String(block?.p || '');
    if (!heading || heading.length > 48 || heading.includes('?')) {
        return false;
    }
    if (/<table\b|<ul\b|<ol\b/i.test(body)) {
        return false;
    }
    const paragraphs = body.match(/<p\b/gi) || [];
    if (paragraphs.length < 2) {
        return false;
    }
    if ((body.match(/<a\b/gi) || []).length > 1) {
        return false;
    }
    return plainHeadingText(body).length >= 120;
}

export function isDisclaimerHeading(heading: unknown, blocks?: Array<{h?: unknown; p?: unknown}> | null): boolean {
    const list = blocks || [];
    for (let index = list.length - 1; index >= 0; index--) {
        const text = plainHeadingText(list[index]?.h);
        if (!text && !plainHeadingText(list[index]?.p)) {
            continue;
        }
        return isTrailingLegalBlock(list[index]) && normalizeLoose(text) === normalizeLoose(heading);
    }
    return false;
}

export function normalizeLoose(value: unknown): string {
    return plainHeadingText(value)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim();
}

function pageWords(pageText: unknown): Set<string> {
    const words = normalizeLoose(pageText).split(/\s+/).filter((word) => word.length >= 2);
    return new Set(words);
}

function tokenGrounded(token: string, words: Set<string>): boolean {
    if (token.length <= 2 || FUNCTION_WORD.has(token) || /^\d+$/.test(token)) {
        return true;
    }
    if (words.has(token)) {
        return true;
    }
    for (const word of words) {
        if (token.length >= 4 && (word.startsWith(token) || token.startsWith(word))) {
            return true;
        }
        const size = Math.min(6, token.length, word.length);
        if (size >= 5 && token.slice(0, size) === word.slice(0, size)) {
            return true;
        }
    }
    return false;
}

function contentTokens(value: string): string[] {
    return normalizeLoose(value).split(/\s+/).filter((token) => token && !FUNCTION_WORD.has(token) && !/^\d+$/.test(token));
}

/** True when a suggested topic names something the scraped page does not say. */
export function isOffPageCompetitorTopic(keyword: unknown, pageText: unknown): boolean {
    const text = plainHeadingText(keyword);
    const words = pageWords(pageText);
    if (!text || !words.size) {
        return false;
    }
    const tokens = contentTokens(text);
    if (!tokens.length) {
        return false;
    }
    return tokens.some((token) => !tokenGrounded(token, words));
}

export function pageCorpus(data: Record<string, unknown> | null | undefined): string {
    const bits: string[] = [];
    const source = data || {};
    for (let level = 1; level <= 6; level++) {
        const parts = source['h' + level];
        if (!Array.isArray(parts)) {
            continue;
        }
        for (const part of parts) {
            const row = part as {h?: unknown; p?: unknown};
            bits.push(String(row?.h || ''), String(row?.p || ''));
        }
    }
    const questions = Array.isArray(source.questions) ? source.questions : [];
    for (const item of questions) {
        const row = item as {question?: unknown; answer?: unknown};
        bits.push(String(row?.question || ''), String(row?.answer || ''));
    }
    return bits.join('\n');
}

export function extractTables(html: unknown): string[] {
    return String(html || '').match(/<table\b[\s\S]*?<\/table>/gi) || [];
}

/**
 * Analyze fills sections by heading. Placeholder rows after the disclaimer
 * are the empty H2s ("Write the heading here"). Do not append them.
 * Other articles still get one slot per completion.
 */
export function placeholderSlotsToAppend(isAnalyze: boolean, part: string, completionCount: number): number {
    if (isAnalyze && part !== 'h1') {
        return 0;
    }
    return Math.max(0, completionCount || 0);
}

export function isEmptyHeadingSlot(block: {h?: unknown; p?: unknown} | null | undefined): boolean {
    return !plainHeadingText(block?.h) && !headingHasBody(block?.p);
}

/**
 * A titled H2 whose paragraph is empty, when another slot already has that heading.
 * The copy that has a paragraph stays. A slot with neither heading nor paragraph is always dropped.
 */
export function shouldDropTitledEmptyDuplicate(
    blocks: Array<{h?: unknown; p?: unknown}> | null | undefined,
    index: number,
): boolean {
    const list = blocks || [];
    const block = list[index];
    if (!block) {
        return false;
    }
    if (isEmptyHeadingSlot(block)) {
        return true;
    }
    const title = plainHeadingText(block.h);
    if (!title || headingHasBody(block.p)) {
        return false;
    }
    return list.some((other, otherIndex) => otherIndex !== index && plainHeadingText(other.h) === title);
}

export type AnalyzeHeadingWrite =
    | {action: 'skip'}
    | {action: 'update'; index: number; heading: string}
    | {action: 'append'; heading: string};

/**
 * Match the visible heading. An empty paragraph never opens a new H2 and never
 * blanks a section that already has text.
 */
export function planAnalyzeHeadingWrite(
    blocks: Array<{h?: unknown; p?: unknown}> | null | undefined,
    visibleHeading: unknown,
    body: unknown,
): AnalyzeHeadingWrite {
    const heading = plainHeadingText(visibleHeading);
    const list = blocks || [];
    if (!heading || !headingHasBody(body)) {
        return {action: 'skip'};
    }
    const index = findHeadingIndex(list, heading);
    if (index >= 0) {
        return {action: 'update', index, heading: plainHeadingText(list[index]?.h) || heading};
    }
    return {action: 'append', heading};
}

export function withoutEmptyHeadingSlots<T extends {h?: unknown; p?: unknown}>(blocks: T[] | null | undefined): T[] {
    return (blocks || []).filter((block) => !isEmptyHeadingSlot(block));
}

function isCapitalWord(word: string): boolean {
    return word.length > 0 && word !== word.toLocaleLowerCase('cs') && word[0] === word[0].toLocaleUpperCase('cs');
}

function nearDigit(text: string, index: number, length: number): boolean {
    const before = text.slice(Math.max(0, index - 2), index);
    const after = text.slice(index + length, index + length + 2);
    return /\d/.test(before) || /\d/.test(after);
}

/**
 * Proper names the page does not contain: a capitalized run, a mid-sentence
 * proper noun, or an all-caps token. Lowercase phrases are not organizations.
 */
export function offPageOrganizations(html: unknown, pageText: unknown): string[] {
    const plain = plainHeadingText(html);
    const page = normalizeLoose(pageText);
    const found: string[] = [];
    const seen = new Set<string>();
    const consider = (label: string) => {
        const key = normalizeLoose(label);
        if (!key || key.length < 3 || seen.has(key) || (page && page.includes(key))) {
            return;
        }
        seen.add(key);
        found.push(normalizeLoose(label));
    };
    const sentences = plain.split(/(?<=[.!?])\s+|\n+/);
    for (const sentence of sentences) {
        const words = sentence.match(/\p{L}[\p{L}'’.-]*/gu) || [];
        let run: string[] = [];
        let runStartedMid = false;
        const flush = () => {
            if (run.length >= 2) {
                consider(run.join(' '));
            } else if (run.length === 1) {
                const only = run[0];
                const allCaps = only === only.toLocaleUpperCase('cs') && only.length >= 3;
                if (allCaps || runStartedMid) {
                    consider(only);
                }
            }
            run = [];
            runStartedMid = false;
        };
        words.forEach((word, index) => {
            if (!isCapitalWord(word) || FUNCTION_WORD.has(normalizeLoose(word))) {
                flush();
                return;
            }
            if (!run.length) {
                runStartedMid = index > 0;
            }
            run.push(word);
        });
        flush();
    }
    for (const match of plain.matchAll(/\b\p{Lu}{3,}\b/gu)) {
        const token = match[0];
        if (nearDigit(plain, match.index || 0, token.length)) {
            continue;
        }
        consider(token);
    }
    return found;
}

/**
 * One regeneration. A clean redraft is kept. A second miss keeps the original
 * section, without deleting words out of the draft.
 */
export function settleSectionOrganizations(
    original: unknown,
    draft: unknown,
    pageText: unknown,
    redraft?: unknown
): string {
    const first = String(draft || '');
    if (!offPageOrganizations(first, pageText).length) {
        return first;
    }
    if (redraft !== undefined) {
        const second = String(redraft || '');
        if (second.trim() && !offPageOrganizations(second, pageText).length) {
            return second;
        }
    }
    return String(original || '');
}

export function restoreStructuredTables(html: unknown, tables: string[]): string {
    const current = String(html || '');
    if (!tables.length) {
        return current;
    }
    const kept = extractTables(current);
    if (kept.length >= tables.length) {
        return current;
    }
    const keptText = new Set(kept.map((table) => normalizeLoose(table)));
    const missing = tables.filter((table) => !current.includes(table) && !keptText.has(normalizeLoose(table)));
    if (!missing.length) {
        return current;
    }
    const prose = current.trim();
    return prose ? `${prose}${missing.join('')}` : missing.join('');
}

/**
 * Response → stored HTML. Reformats the model string, then keeps the original
 * section when that rewrite invents a fact or a table.
 */
export function finalizeAnalyzedBody(
    body: unknown,
    pageText: unknown,
    original?: unknown,
    movableHeadings: string[] = [],
    ownHeading: unknown = ''
): string {
    const raw = String(body ?? '');
    const html = reformatOpenAiResponseToHtml(raw) || '';
    if (original === undefined) {
        return restoreStructuredTables(html, extractTables(raw));
    }
    return guardAnalyzedSection(original, html, pageText, movableHeadings, ownHeading);
}

/** Editor getData, then put back a table or in-section heading the schema dropped. */
export function htmlAfterEditor(sourceHtml: unknown, editorHtml: unknown): string {
    const source = String(sourceHtml || '');
    const edited = restoreStructuredTables(editorHtml, extractTables(source));
    return mergeBodyKeepingTables(source, edited);
}

/**
 * Suggested URL into the first H2. Wait while that paragraph is still empty.
 * Warn only after content has settled and the paragraph is still missing.
 * Never invent a heading to hold the link.
 */
export function suggestedH2UrlAction(paragraph: unknown, contentSettled: boolean): 'insert' | 'wait' | 'warn' {
    if (String(paragraph || '').trim()) {
        return 'insert';
    }
    return contentSettled ? 'warn' : 'wait';
}

function samePlain(left: string, right: string): boolean {
    return plainHeadingText(left) === plainHeadingText(right);
}

function stripInventedStructure(html: string): string {
    return String(html || '')
        .replace(/<table\b[\s\S]*?<\/table>/gi, '')
        .replace(/<h[3-6]\b[^>]*>[\s\S]*?<\/h[3-6]>/gi, '');
}

function protectedChunks(html: string): string[] {
    const re = /<table\b[\s\S]*?<\/table>|<h[3-6]\b[^>]*>[\s\S]*?<\/h[3-6]>(?:\s*<(?:ul|ol)\b[\s\S]*?<\/(?:ul|ol)>)*/gi;
    return String(html || '').match(re) || [];
}

export function headingTextsIn(html: unknown): string[] {
    const titles: string[] = [];
    const re = /<h[1-6]\b[^>]*>([\s\S]*?)<\/h[1-6]>/gi;
    let match: RegExpExecArray | null;
    const text = String(html || '');
    while ((match = re.exec(text))) {
        const title = plainHeadingText(match[1]);
        if (title) {
            titles.push(title);
        }
    }
    return titles;
}

function restoreProtectedChunks(previous: string, stripped: string): string {
    const chunks = protectedChunks(previous);
    if (!chunks.length) {
        return stripped.trim();
    }
    const plain = plainHeadingText(stripped);
    const missing: string[] = [];
    for (const chunk of chunks) {
        if (stripped.includes(chunk)) {
            continue;
        }
        if (/<table\b/i.test(chunk)) {
            const seen = extractTables(stripped).some((table) => samePlain(table, chunk));
            if (!seen) {
                missing.push(chunk);
            }
            continue;
        }
        const title = headingTextsIn(chunk)[0] || '';
        if (title && plain.includes(plainHeadingText(title))) {
            continue;
        }
        missing.push(chunk);
    }
    if (!missing.length) {
        return stripped.trim();
    }
    const prose = stripped.trim();
    return prose ? `${prose}${missing.join('')}` : missing.join('');
}

/**
 * Original tables and subordinate headings are protected blocks.
 * A table the model wrote is discarded. The source copy is spliced back.
 */
export function mergeBodyKeepingTables(previous: unknown, next: unknown): string {
    const prev = String(previous || '');
    const incoming = String(next || '');
    if (!incoming.trim()) {
        return prev;
    }
    const stripped = stripInventedStructure(incoming).trim();
    if (!prev.trim()) {
        return stripped;
    }
    if (samePlain(prev, incoming) || samePlain(prev, stripped)) {
        // User edits such as splitting one <p> into several keep the same plain text.
        if (stripped && stripped !== prev.trim()) {
            return restoreProtectedChunks(prev, stripped);
        }
        return prev;
    }
    return restoreProtectedChunks(prev, stripped);
}

const DASH = '[\\u2010-\\u2015\\-]';
const AMOUNT = '(\\d{1,3}(?:[ \\u00a0]\\d{3})+(?:[.,]\\d+)?|\\d+(?:[.,]\\d+)?)';
const SCALE = '((?:tis(?:íc(?:e|ů|u)?)?|mil(?:ion(?:y|ů|u)?)?|mld|miliard(?:a|y|ů)?|thousand|million|billion)\\.?)';
const CURRENCY = '(Kč|CZK|EUR|USD|€|\\$)';
const ISIN_RE = /\b[A-Z]{2}[A-Z0-9]{9,10}\b/g;
const PERCENT_RE = /(\d+(?:[.,]\d+)?)\s*%/g;
const PERCENT_RANGE_RE = new RegExp(`(\\d+(?:[.,]\\d+)?)\\s*${DASH}\\s*(\\d+(?:[.,]\\d+)?)\\s*%`, 'g');
const COUPON_RE = /\b(\d{1,3},\d{2})\b/g;
const DATE_RE = /\b(\d{1,2}[./]\d{1,2}[./]\d{2,4})\b/g;
const MONTH_RE = /\b(\d{1,2}\/\d{4})\b/g;
const MONTH_COUNT_RE = new RegExp(`(\\d{1,3})(?:\\s*${DASH}\\s*(\\d{1,3}))?\\s*m[eě]s[ií]c(?:e|ů|u|um)?`, 'gi');
const YEAR_RE = /\b((?:19|20)\d{2})\b/g;
const MONEY_RANGE_RE = new RegExp(`${AMOUNT}\\s*${DASH}\\s*${AMOUNT}(?:\\s+${SCALE})?\\s*${CURRENCY}`, 'gi');
const MONEY_RE = new RegExp(`${AMOUNT}(?:\\s+${SCALE})?\\s*${CURRENCY}`, 'gi');
const GROUP_RE = /\b(\d{1,3}(?:[ \u00a0]\d{3}){1,6}(?:[.,]\d+)?)\b/g;

function numericKey(value: string): string {
    const parsed = Number(value.replace(/[^\d.,]/g, '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed.toFixed(2) : '';
}

function allMatches(re: RegExp, text: string): string[] {
    re.lastIndex = 0;
    return Array.from(text.matchAll(re)).map((match) => match[0]);
}

function digitKey(value: string): string {
    return value.replace(/[^\d]/g, '');
}

function currencyFamily(token: string): string {
    const key = token.toUpperCase();
    if (key === 'KČ' || key === 'CZK') {
        return 'CZK';
    }
    if (key === '€' || key === 'EUR') {
        return 'EUR';
    }
    if (key === '$' || key === 'USD') {
        return 'USD';
    }
    return key;
}

function dateKey(value: string): string {
    const parts = value.split(/[./]/).map((part) => String(Number(part)));
    return parts.some((part) => part === 'NaN') ? '' : parts.join('.');
}

function scaleFamily(token: string): string {
    const key = token
        .toLocaleLowerCase('cs')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\./g, '');
    if (/^tis/.test(key) || key === 'thousand') {
        return 'tis';
    }
    if (key === 'mil' || /^milion/.test(key) || key === 'million') {
        return 'mil';
    }
    if (/^mld|^miliard/.test(key) || key === 'billion') {
        return 'mld';
    }
    return key;
}

function moneyFact(amount: string, scale: string | undefined, currency: string): string {
    const digits = digitKey(amount);
    if (!digits) {
        return '';
    }
    return `${digits}:${scale ? scaleFamily(scale) : ''}:${currencyFamily(currency)}`;
}

function moneyFacts(text: string): {keys: string[]; occupied: Array<{start: number; end: number}>} {
    const keys: string[] = [];
    const occupied: Array<{start: number; end: number}> = [];
    const take = (match: RegExpMatchArray, left: number, right: number | undefined, scale: number, currency: number) => {
        const scaleToken = match[scale];
        const unit = match[currency];
        const first = moneyFact(match[left], scaleToken, unit);
        if (first) {
            keys.push(first);
        }
        if (right) {
            const second = moneyFact(match[right], scaleToken, unit);
            if (second) {
                keys.push(second);
            }
        }
        occupied.push({start: match.index || 0, end: (match.index || 0) + match[0].length});
    };
    MONEY_RANGE_RE.lastIndex = 0;
    for (const match of text.matchAll(MONEY_RANGE_RE)) {
        take(match, 1, 2, 3, 4);
    }
    MONEY_RE.lastIndex = 0;
    for (const match of text.matchAll(MONEY_RE)) {
        const start = match.index || 0;
        const end = start + match[0].length;
        if (occupied.some((span) => start < span.end && end > span.start)) {
            continue;
        }
        take(match, 1, undefined, 2, 3);
    }
    return {keys, occupied};
}

function moneyKeys(text: string): string[] {
    return moneyFacts(text).keys;
}

function percentKeys(text: string): string[] {
    const keys: string[] = [];
    PERCENT_RANGE_RE.lastIndex = 0;
    for (const match of text.matchAll(PERCENT_RANGE_RE)) {
        keys.push(numericKey(match[1]), numericKey(match[2]));
    }
    for (const token of [...allMatches(PERCENT_RE, text), ...allMatches(COUPON_RE, text)]) {
        keys.push(numericKey(token));
    }
    return keys.filter(Boolean);
}

function monthCountKeys(text: string): string[] {
    const keys: string[] = [];
    MONTH_COUNT_RE.lastIndex = 0;
    for (const match of text.matchAll(MONTH_COUNT_RE)) {
        const left = Number(match[1]);
        if (Number.isFinite(left)) {
            keys.push(String(left));
        }
        if (match[2]) {
            const right = Number(match[2]);
            if (Number.isFinite(right)) {
                keys.push(String(right));
            }
        }
    }
    return keys;
}

function groupKeys(text: string): string[] {
    const occupied = moneyFacts(text).occupied;
    GROUP_RE.lastIndex = 0;
    const keys: string[] = [];
    for (const match of text.matchAll(GROUP_RE)) {
        const start = match.index || 0;
        const end = start + match[0].length;
        if (occupied.some((span) => start < span.end && end > span.start)) {
            continue;
        }
        keys.push(digitKey(match[1]));
    }
    return keys;
}

const THOUSAND_SEP = ' \\u00a0\\u2007\\u2008\\u2009\\u202f\\u200a';
const NUMBER_TOKEN_RE = new RegExp(
    `\\d{1,3}(?:[${THOUSAND_SEP}]\\d{3})+(?:[.,]\\d+)?|\\d+(?:[.,]\\d+)?`,
    'g'
);
const SCALE_WORD = 'tis(?:íc(?:e|ů|u)?)?|mil(?:ion(?:y|ů|u)?)?|mld|miliard(?:a|y|ů)?|thousand|million|billion';
const SCALE_MULTIPLIER: Record<string, number> = {tis: 1_000, mil: 1_000_000, mld: 1_000_000_000};
const RANGE_DASH = /[\u2010-\u2015-]/;

/**
 * List markers ("1. ", "2)", "3. Title") and a standalone 0, 1, or 2
 * used as an index or a word are not facts. A decimal such as 2.0 stays
 * a decimal: it does not match the integer 2. A range end such as 2–5 stays.
 */
function skipIncidentalNumber(text: string, start: number, raw: string): boolean {
    const compact = raw.replace(new RegExp(`[${THOUSAND_SEP}]`, 'g'), '');
    if (/^\d{1,2}$/.test(compact)) {
        const next = text[start + raw.length] || '';
        const after = text[start + raw.length + 1] || '';
        if ((next === '.' || next === ')') && (after === '' || /\s/.test(after))) {
            return true;
        }
    }
    if (!/^[012]$/.test(compact)) {
        return false;
    }
    const prev = start > 0 ? text[start - 1] : '';
    const next = text[start + raw.length] || '';
    if (RANGE_DASH.test(prev) || RANGE_DASH.test(next)) {
        return false;
    }
    if (scaleAfter(text, start + raw.length)) {
        return false;
    }
    return true;
}

function scaleAfter(text: string, index: number): string {
    const rest = text.slice(index);
    const ranged = new RegExp(
        `^\\s*[\\u2010-\\u2015-]\\s*(?:\\d{1,3}(?:[${THOUSAND_SEP}]\\d{3})+|\\d+)(?:[.,]\\d+)?\\s+(${SCALE_WORD})\\b`,
        'i'
    ).exec(rest);
    if (ranged?.[1]) {
        return scaleFamily(ranged[1]);
    }
    const direct = new RegExp(`^\\s*(${SCALE_WORD})\\b`, 'i').exec(rest);
    if (direct?.[1]) {
        return scaleFamily(direct[1]);
    }
    return '';
}

function decimalFactKey(raw: string): string {
    const norm = raw.replace(',', '.');
    const [whole, frac = ''] = norm.split('.');
    const trimmed = frac.replace(/0+$/, '');
    return `${Number(whole) || 0}.${trimmed || '0'}`;
}

function canonicalNumberKey(compact: string, scale: string): string {
    let key = '';
    if (/[.,]\d/.test(compact)) {
        key = `d:${decimalFactKey(compact)}`;
    } else if (/^(?:19|20)\d{2}$/.test(compact)) {
        key = `y:${compact}`;
    } else {
        const parsed = Number(compact);
        if (Number.isFinite(parsed)) {
            key = `n:${parsed}`;
        }
    }
    if (!key) {
        return '';
    }
    return scale ? `${key}:${scale}` : key;
}

function splitNumberKey(key: string): {kind: string; value: string; scale: string} {
    const kind = key.slice(0, 2);
    const body = key.slice(2);
    const scale = /:(tis|mil|mld)$/.exec(body);
    if (!scale) {
        return {kind, value: body, scale: ''};
    }
    return {kind, value: body.slice(0, -scale[0].length), scale: scale[1]};
}

function numberKeyAliases(key: string): string[] {
    const parts = splitNumberKey(key);
    const aliases = new Set<string>([key]);
    if (parts.kind !== 'n:' || !parts.value) {
        return [key];
    }
    const base = Number(parts.value);
    if (!Number.isFinite(base)) {
        return [key];
    }
    if (parts.scale) {
        const mult = SCALE_MULTIPLIER[parts.scale];
        if (mult) {
            aliases.add(`n:${base * mult}`);
        }
    } else if (base >= 1_000) {
        for (const [scale, mult] of Object.entries(SCALE_MULTIPLIER)) {
            if (base % mult === 0) {
                const scaled = base / mult;
                if (scaled > 0 && scaled < 10_000) {
                    aliases.add(`n:${scaled}:${scale}`);
                }
            }
        }
    }
    return [...aliases];
}

function keySetIncludes(requiredKey: string, present: Set<string>): boolean {
    return numberKeyAliases(requiredKey).some((alias) => present.has(alias));
}

/**
 * One identity for a numeric fact. Spaces, nbsp, and thin spaces are thousand
 * separators. Comma and dot are the same decimal separator. Currency and a
 * percent sign are not part of the value. A scale word (tisíc, million) is.
 */
function numberFactKeys(text: string): string[] {
    const cleaned = String(text || '');
    NUMBER_TOKEN_RE.lastIndex = 0;
    const keys: string[] = [];
    for (const match of cleaned.matchAll(NUMBER_TOKEN_RE)) {
        const raw = match[0];
        const start = match.index || 0;
        if (skipIncidentalNumber(cleaned, start, raw)) {
            continue;
        }
        const compact = raw.replace(new RegExp(`[${THOUSAND_SEP}]`, 'g'), '');
        const key = canonicalNumberKey(compact, scaleAfter(cleaned, start + raw.length));
        if (key) {
            keys.push(key);
        }
    }
    return keys;
}

function hasFact(text: string): boolean {
    return percentKeys(text).length > 0
        || moneyKeys(text).length > 0
        || monthCountKeys(text).length > 0
        || allMatches(ISIN_RE, text).length > 0
        || allMatches(DATE_RE, text).length > 0
        || allMatches(MONTH_RE, text).length > 0
        || allMatches(YEAR_RE, text).length > 0
        || groupKeys(text).length > 0
        || numberFactKeys(text).length > 0;
}

/**
 * Scraped page text only. A writing brief and the current calendar year are
 * not a source of numbers for URL-improve.
 */
export function analyzeFactCorpus(pageText: unknown): string {
    return String(pageText || '');
}

/**
 * Every ISIN, percentage, amount, date, year, and other number in the output
 * must already be on the source page (including text the page itself cites).
 * No name list. The caller must not pass the writing brief as the source.
 */
export function sectionFactsGrounded(output: unknown, source: unknown): boolean {
    const out = plainHeadingText(output);
    const src = plainHeadingText(source);
    if (!out) {
        return true;
    }
    if (!src) {
        return !hasFact(out);
    }
    const upper = src.toUpperCase();
    for (const isin of allMatches(ISIN_RE, out)) {
        if (!upper.includes(isin.toUpperCase())) {
            return false;
        }
    }
    const dates = new Set(allMatches(DATE_RE, src).map((token) => dateKey(token)));
    for (const token of allMatches(DATE_RE, out)) {
        if (!dates.has(dateKey(token))) {
            return false;
        }
    }
    const months = new Set(allMatches(MONTH_RE, src));
    for (const token of allMatches(MONTH_RE, out)) {
        if (!months.has(token)) {
            return false;
        }
    }
    const monthCounts = new Set(monthCountKeys(src));
    for (const key of monthCountKeys(out)) {
        if (!monthCounts.has(key)) {
            return false;
        }
    }
    const numbers = new Set(numberFactKeys(src));
    for (const key of numberFactKeys(out)) {
        if (!keySetIncludes(key, numbers)) {
            return false;
        }
    }
    return true;
}

export function collectMovableHeadings(data: Record<string, unknown> | null | undefined): string[] {
    const titles: string[] = [];
    const source = data || {};
    for (let level = 1; level <= 6; level++) {
        const parts = source['h' + level];
        if (!Array.isArray(parts)) {
            continue;
        }
        for (const part of parts) {
            const row = part as {h?: unknown; p?: unknown};
            if (level >= 3) {
                const heading = plainHeadingText(row?.h);
                if (heading) {
                    titles.push(heading);
                }
            }
            titles.push(...headingTextsIn(row?.p));
        }
    }
    return titles;
}

export function sectionIntroducesForeignHeading(
    body: unknown,
    ownBody: unknown,
    ownHeading: unknown,
    catalog: string[]
): boolean {
    const own = new Set<string>();
    const add = (value: unknown) => {
        const key = normalizeLoose(value);
        if (key) {
            own.add(key);
        }
    };
    add(ownHeading);
    headingTextsIn(ownBody).forEach(add);
    const plain = normalizeLoose(body);
    if (!plain) {
        return false;
    }
    for (const title of catalog) {
        const key = normalizeLoose(title);
        if (!key || key.length < 8 || own.has(key)) {
            continue;
        }
        if (plain.includes(key)) {
            return true;
        }
    }
    return false;
}

export type SettledAnalyzePart = {
    state: "done";
    value1: string;
    value2: string;
};

/**
 * A URL-improve part that did not become a new grounded section is not a failure.
 * An existing page section keeps its scraped heading and body, including an empty body.
 * An extra keyword that stayed blank, a raw keyword, or a template heading is skipped
 * as an empty done slot. Neither case is state "failed", and neither is replaced
 * with the new-article template ("Jak funguje" / "Jak souvisí").
 * A complete heading+body pair returns null so the caller keeps the generated text.
 */
export function settleRejectedAnalyzePart(input: {
    part: string;
    hasSource: boolean;
    sourceHeading?: string;
    sourceBody?: string;
    value1?: string;
    value2?: string;
}): SettledAnalyzePart | null {
    if (input.part === "meta" || input.part === "question") {
        return null;
    }
    const value1 = plainHeadingText(input.value1);
    const value2 = plainHeadingText(input.value2);
    if (value1 && value2) {
        return null;
    }
    if (input.hasSource) {
        return {
            state: "done",
            value1: plainHeadingText(input.sourceHeading || input.value1 || ""),
            value2: String(input.sourceBody || ""),
        };
    }
    return {state: "done", value1: "", value2: ""};
}

/** Keep the original section when a rewrite invents a fact or moves another section's heading. */
export function guardAnalyzedSection(
    original: unknown,
    generated: unknown,
    pageText: unknown,
    movableHeadings: string[] = [],
    ownHeading: unknown = ''
): string {
    const prev = String(original || '');
    const next = String(generated || '');
    if (!next.trim()) {
        return prev;
    }
    const merged = mergeBodyKeepingTables(prev, next);
    const corpus = `${String(pageText || '')}\n${prev}`;
    if (!sectionFactsGrounded(merged, corpus)) {
        return prev;
    }
    if (sectionIntroducesForeignHeading(merged, prev, ownHeading, movableHeadings)) {
        return prev;
    }
    return merged;
}

export function headingHasBody(body: unknown): boolean {
    return !!(plainHeadingText(String(body || '').replace(/<[^>]+>/g, ' ')) || extractTables(body).length);
}

function levelBlocks(data: Record<string, unknown>, level: number): Array<{h: string; p: string}> {
    const parts = data['h' + level];
    if (!Array.isArray(parts)) {
        return [];
    }
    return parts.map((part) => {
        const row = part as {h?: unknown; p?: unknown};
        return {h: plainHeadingText(row?.h), p: String(row?.p || '')};
    }).filter((row) => row.h);
}

function bodyForHeading(data: Record<string, unknown>, part: string, heading: string): string {
    const level = Number(String(part).replace('h', ''));
    if (!level) {
        return '';
    }
    const key = normalizeLoose(heading);
    const match = levelBlocks(data, level).find((row) => normalizeLoose(row.h) === key);
    return match ? match.p : '';
}

function emptyArticle(): AssembledAnalyzeArticle {
    return {h1: [], h2: [], h3: [], h4: [], h5: [], h6: [], questions: []};
}

/**
 * Build the enhance request from the outline and echo each section through
 * `complete` (tests pass the section back unchanged). Headings stay plain
 * text. Tables from the scraped section are kept. Empty H3+ bodies are
 * dropped. A heading is written once.
 */
export function assembleAnalyzeEnhance(
    data: Record<string, unknown>,
    outline: OutlineLike[],
    complete: (section: AnalyzeSectionInput) => AnalyzeSectionEcho
): AssembledAnalyzeArticle {
    const article = emptyArticle();
    const seen = new Set<string>();

    const pushHeading = (part: keyof AssembledAnalyzeArticle, heading: string, body: string): void => {
        if (part === 'questions') {
            return;
        }
        const text = plainHeadingText(heading);
        if (!text) {
            return;
        }
        const level = Number(String(part).replace('h', ''));
        if (level >= 3 && !headingHasBody(body)) {
            return;
        }
        const key = normalizeLoose(text);
        if (!key || seen.has(key)) {
            return;
        }
        seen.add(key);
        article[part].push({h: text, p: body || ''});
    };

    const page = pageCorpus(data);
    const movable = collectMovableHeadings(data);
    const pageH1 = plainHeadingText((Array.isArray(data.h1) ? (data.h1[0] as {h?: unknown})?.h : '') || '');
    const h1Slot = outline.find((slot) => slot.part === 'h1');
    const h1Heading = pageH1 || plainHeadingText(h1Slot?.keyword);
    const h1Body = String((Array.isArray(data.h1) ? (data.h1[0] as {p?: unknown})?.p : '') || '');
    if (h1Heading) {
        const echoed = complete({
            part: 'h1',
            heading: h1Heading,
            body: h1Body,
            keyword: h1Heading,
        });
        const body = finalizeAnalyzedBody(echoed.body ?? h1Body, page, h1Body, movable, h1Heading);
        pushHeading('h1', echoed.heading || h1Heading, body);
    }

    for (const slot of outline) {
        if (slot.part === 'h1' || slot.part === 'meta') {
            continue;
        }
        const heading = plainHeadingText(slot.keyword);
        if (!heading) {
            continue;
        }
        if (slot.part === 'question') {
            const echoed = complete({
                part: 'question',
                heading,
                body: '',
                keyword: heading,
            });
            const question = plainHeadingText(echoed.heading || heading);
            const key = normalizeLoose(question);
            if (!question || !key || seen.has(key)) {
                continue;
            }
            seen.add(key);
            article.questions.push({
                question,
                answer: finalizeAnalyzedBody(echoed.body || '', page, '', movable, heading),
            });
            continue;
        }
        const body = bodyForHeading(data, slot.part, heading);
        const echoed = complete({
            part: slot.part,
            heading,
            body,
            keyword: heading,
        });
        const nextHeading = plainHeadingText(echoed.heading || heading);
        const nextBody = finalizeAnalyzedBody(echoed.body ?? body, page, body, movable, heading);
        if (slot.part === 'h2' || slot.part === 'h3' || slot.part === 'h4' || slot.part === 'h5' || slot.part === 'h6') {
            pushHeading(slot.part, nextHeading, nextBody);
        }
    }

    const placed = new Set<string>();
    for (const block of [...article.h1, ...article.h2]) {
        headingTextsIn(block.p).forEach((title) => placed.add(normalizeLoose(title)));
    }
    for (let level = 3; level <= 6; level++) {
        const part = ('h' + level) as 'h3' | 'h4' | 'h5' | 'h6';
        for (const block of levelBlocks(data, level)) {
            if (!headingHasBody(block.p)) {
                continue;
            }
            if (placed.has(normalizeLoose(block.h))) {
                continue;
            }
            pushHeading(part, block.h, finalizeAnalyzedBody(block.p, page, block.p, movable, block.h));
        }
    }

    return article;
}

export function findHeadingIndex(blocks: Array<{h?: unknown}> | null | undefined, heading: string): number {
    const key = normalizeLoose(heading);
    if (!key || !blocks) {
        return -1;
    }
    return blocks.findIndex((block) => normalizeLoose(block?.h) === key);
}

/** Index for a keyword section that is not already on the page: before the trailing legal block. */
export function insertBeforeDisclaimer(blocks: Array<{h?: unknown; p?: unknown}> | null | undefined): number {
    const list = blocks || [];
    for (let index = list.length - 1; index >= 0; index--) {
        if (!plainHeadingText(list[index]?.h) && !plainHeadingText(list[index]?.p)) {
            continue;
        }
        return isTrailingLegalBlock(list[index]) ? index : list.length;
    }
    return list.length;
}

/**
 * Drop a leading line or element whose whole text is the heading.
 * A later sentence that only mentions the topic stays.
 */
export function stripLeadingHeading(body: unknown, heading: unknown): string {
    const expected = normalizeLoose(heading);
    if (!expected) {
        return String(body || '').trim();
    }
    let text = String(body || '').replace(/^\uFEFF/, '');
    const element = /^\s*<(h[1-6]|p|strong|b)\b[^>]*>([\s\S]*?)<\/\1>\s*/i.exec(text);
    if (element && normalizeLoose(element[2]) === expected) {
        text = text.slice(element[0].length);
    }
    const lines = text.split(/\r?\n/);
    if (lines.length && normalizeLoose(lines[0]) === expected) {
        let index = 1;
        while (index < lines.length && normalizeLoose(lines[index]) === '') {
            index += 1;
        }
        text = lines.slice(index).join('\n');
    }
    return text.trim();
}
